import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Wifi, Loader2, Copy, ArrowLeft } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

// -----------------------------------------------------------------------------
// /wifi/:tag_id — public Wi-Fi share page. Renders a WIFI:… QR code that
// modern phone cameras auto-detect and offer a "Join network" prompt.
// -----------------------------------------------------------------------------
export default function TapWifi() {
  const { tag_id } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/nfc/wifi/${tag_id}`)
      .then(r => setW(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Not a Wi-Fi tag"));
  }, [tag_id]);

  useSeo({ title: "Guest Wi-Fi · tap to join · find2near",
           description: "Scan the QR to join Wi-Fi without asking for the password.",
           canonical: `/wifi/${tag_id}` });

  const copy = async (v, label) => { try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); } catch { /* ignore */ } };

  if (err) return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="text-center">
        <Wifi className="mx-auto text-stone-400" size={44}/>
        <div className="font-black text-2xl text-stone-900 mt-3">{err}</div>
        <Link to="/nfc" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:underline"><ArrowLeft size={13}/> Back to NFC hub</Link>
      </div>
    </div>
  );
  if (!w) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-800 to-teal-950 text-white flex flex-col items-center justify-center px-4 py-10" data-testid="tap-wifi-page">
      <div className="text-[11px] uppercase tracking-widest font-black opacity-70 flex items-center gap-2"><Wifi size={12}/> Guest Wi-Fi</div>
      <h1 className="heading text-3xl md:text-4xl font-black mt-1">Join <span className="text-emerald-300">{w.ssid}</span></h1>
      <div className="text-sm opacity-80 mt-1 text-center max-w-sm">Open your camera and point at the QR — your phone will prompt to join.</div>

      <div className="bg-white rounded-3xl p-4 mt-6 shadow-2xl" data-testid="tap-wifi-qr">
        <QRCodeSVG value={w.wifi_string} size={240} bgColor="#ffffff" fgColor="#111111" level="M"/>
      </div>

      <div className="mt-5 rounded-2xl bg-white/10 backdrop-blur border border-white/20 p-3 text-xs text-white/80 space-y-1 max-w-sm w-full">
        <div className="flex justify-between"><span>SSID</span><button onClick={() => copy(w.ssid, "SSID")} className="font-bold text-white hover:opacity-80 inline-flex items-center gap-1">{w.ssid} <Copy size={10}/></button></div>
        <div className="flex justify-between"><span>Encryption</span><span className="font-mono">{w.encryption}</span></div>
        {w.hidden && <div className="flex justify-between"><span>Hidden</span><span className="font-bold">yes</span></div>}
      </div>

      <div className="mt-6 text-[11px] text-white/50">Password never displayed. Share this page via link — the QR carries the credentials safely.</div>
    </div>
  );
}
